
import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { tap } from 'rxjs/operators';
import { setTheme } from './app.reducer';
import { ColorPreset } from '@fitness-force/constants';
import { PrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import Lara from '@primeng/themes/lara';
import Material from '@primeng/themes/material';
import Nora from '@primeng/themes/nora';
import { definePreset } from '@primeng/themes';

@Injectable()
export class ThemeEffects {
  private document = inject<Document>(DOCUMENT);
  private primeng = inject(PrimeNG);

  // Use inject() for dependencies used in class property initializers
  private actions$ = inject(Actions);

  // Effect to handle theme changes and persist to localStorage
  changeTheme$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setTheme),
      tap(({ theme }) => {
        localStorage.setItem('selected-theme', JSON.stringify(theme));
        this.loadTheme(theme);
      })
    ),
    { dispatch: false }
  );
  
  // Helper method to load theme using PrimeNG v19 theme system
  private loadTheme(theme: any) {
    let themeMode = theme.themeMode;
    
    // Handle system preference if theme is set to 'system'
    if (themeMode.toLowerCase() === 'system') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      themeMode = prefersDark ? "dark" : "light";
    }
    
    // Set dark mode class on body
    if (themeMode.toLowerCase() === 'dark') {
      this.document.body.classList.add('dark-mode-enabled');
    } else {
      this.document.body.classList.remove('dark-mode-enabled');
    }
    
    // Map theme name to PrimeNG theme preset
    let themePreset;
    const colors= ColorPreset.find(c=> c.colorName === theme.selectedColor);
    // Determine which preset to use based on theme name
    if (theme.selectedTheme === 'Lara') {
      themePreset = definePreset(Lara, {
        semantic: {
          primary: colors?.primary,
          colorScheme: colors?.semantic?.colorScheme
        }
      });
    } else if (theme.selectedTheme === 'Material') {
      themePreset = definePreset(Material, {
        semantic: {
          primary: colors?.primary,
          colorScheme: colors?.semantic?.colorScheme
        }
      });
    } else if (theme.selectedTheme === 'Aura') {
      themePreset = definePreset(Aura, {
        semantic: {
          primary: colors?.primary,
          colorScheme: colors?.semantic?.colorScheme
        }
      });
    } else if (theme.selectedTheme === 'Nora') {
      themePreset = definePreset(Nora, {
        semantic: {
          primary: colors?.primary,
          colorScheme: colors?.semantic?.colorScheme
        }
      });
    } else {
      // Default to Lara
      themePreset = definePreset(Lara, {
        semantic: {
          primary: colors?.primary,
          colorScheme: colors?.semantic?.colorScheme
        }
      });
    }
    
    // Update PrimeNG theme configuration
    this.primeng.theme.set({
      ripple: true,
      inputVariant: 'outlined',
      preset: themePreset,
      options: {
        prefix: 'p',
        darkModeSelector: themeMode === 'dark' ? '.dark-mode-enabled' : '',
        cssLayer: true
      }
    });

  }
}